import { Component, computed, effect, forwardRef, inject, input, OnInit, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { watch } from '@/utils/watch';
import { matchesTokens, tokenizeSearch } from '@/utils/text-search';
import { GenericDialogComponent } from '@components/dialog/generic-dialog/generic-dialog.component';
import { debounceTime, firstValueFrom, Subject } from 'rxjs';
import { NbDialogService } from '@nebular/theme';

export interface PickListItem {
  id: string;
}

export enum AssignmentType {
  ASSIGNED = 'ASSIGNED',
  ASSIGNED_ELSEWHERE = 'ASSIGNED_ELSEWHERE',
  UNASSIGNED = 'UNASSIGNED',
}

export interface PickListBehaviour<Item> {
  //required inputs
  getAssignmentType(item: Item): AssignmentType;

  getDisplayName(item: Item): string;

  //optional inputs
  showToggleAssignedFromOthers?(): boolean;

  alwaysShowAssignedFromOthers?(): boolean;

  getFirstAdditionalInfo?(item: Item): string;

  getSecondAdditionalInfo?(item: Item): string;

  getThirdAdditionalInfo?(item: Item): string;

  getSearchableFields?(item: Item): string[];

  isPaginated: boolean;
}

export interface PaginatedPickListBehaviour<Item> extends PickListBehaviour<Item> {
  getAllItemsWithPagination(params: {
    page?: number;
    size?: number;
    search?: string;
  }): Promise<{ items: Item[]; total: number }>;

  isPaginated: true;
}

export interface UnpaginatedPickListBehaviour<Item> extends PickListBehaviour<Item> {
  getAllItems(): Promise<Item[]>;

  isPaginated: false;
}

@Component({
  standalone: false,
  selector: 'wr-generic-pick-list',
  templateUrl: './generic-pick-list.component.html',
  styleUrl: './generic-pick-list.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenericPickListComponent),
      multi: true,
    },
  ],
})
export class GenericPickListComponent<Item extends PickListItem, Behaviour extends PickListBehaviour<Item>> extends AbstractFormControl<Item[]>
  implements OnInit {

  readonly page = signal(0);

  readonly pageSize = 20;

  readonly nbDialogService = inject(NbDialogService);

  readonly behaviour = input.required<Behaviour>();

  readonly initialSearch = input('');

  readonly items = signal<Item[]>([]);

  /**
   * Laeuft gerade eine Ladeanfrage? Gehoert dem Ladeeffect, nicht dem
   * Scroll-Handler: `items.set([])` schrumpft die Liste, der Browser klemmt
   * `scrollTop` auf 0 und feuert dabei ein `scroll`-Event. Ohne diese Sperre
   * liest `onUnassignedScroll` das als "Ende erreicht" und zaehlt `page` hoch,
   * waehrend Seite 0 noch unterwegs ist.
   */
  private isLoadingItems = false;

  /**
   * Ist bereits ein Seitenwechsel im Debounce unterwegs? Verhindert, dass jedes
   * weitere Scroll-Event `scroll$` erneut speist und damit den `debounceTime`
   * Timer neu startet — sonst laedt bei durchgehendem Scrollen gar nichts nach,
   * bis der Nutzer stehen bleibt.
   */
  private isScrollLoadQueued = false;

  /**
   * Laufende Nummer der letzten Ladeanfrage. Antworten aelterer Anfragen werden
   * verworfen, damit eine verspaetete Antwort keine neueren Treffer ueberschreibt.
   */
  private loadRequestId = 0;


  readonly unassignedItems = computed(() =>
    this.items().filter(i => !this.assignedItemIds().includes(i.id)),
  );

  readonly assignedItemIds = computed(() => {
    const selected = this.valueSignal() ?? [];
    return selected.map(item => item.id);
  });

  readonly searchText = signal('');

  readonly showToggleAlreadyAssignedToOthers = computed(() =>
    this.behaviour().showToggleAssignedFromOthers?.() ?? false,
  );

  readonly alwaysShowAssignedToOthers = computed(() =>
    this.behaviour().alwaysShowAssignedFromOthers?.() ?? false,
  );

  readonly assignedItemsToggleTooltip = computed(() => {
    const show = this.showFromOtherScopes();
    return show
      ? 'Bereits zugewiesene Elemente ausblenden'
      : 'Bereits zugewiesene Elemente anzeigen';
  });

  readonly assignedItemsToggleIcon = computed(() =>
    this.showFromOtherScopes() ? 'eye-outline' : 'eye-off-2-outline',
  );

  /**
   * Jede Stufe ist ein eigenes computed, damit Angular das Ergebnis ueber seine
   * Abhaengigkeiten memoisiert und bei unveraenderten Daten dieselbe
   * Array-Referenz zurueckgibt.
   */
  readonly sortedUnassignedItems = computed(() =>
    this.sortItems(this.unassignedItems()),
  );

  readonly visibleUnassignedItems = computed(() => {
    const showFromOtherScopes = this.showFromOtherScopes() || this.alwaysShowAssignedToOthers();
    const sorted = this.sortedUnassignedItems();

    return showFromOtherScopes
      ? sorted
      : this.withoutAssignedElsewhere(sorted);
  });

  readonly filteredUnassignedItems = computed(() =>
    this.applySearch(this.visibleUnassignedItems()),
  );

  readonly filteredAssignedItems = computed(() => {
    const value = this.valueSignal();
    if (!value) {
      return undefined;
    }
    return this.applySearch(this.sortItems(value));
  });

  readonly showFromOtherScopes = signal(false);

  private readonly selection = signal<ReadonlyMap<string, Item>>(new Map());

  private readonly assignedIdSet = computed(() => new Set(this.assignedItemIds()));

  private readonly selectedUnassignedItems = computed(() => {
    const assignedIds = this.assignedIdSet();
    return [...this.selection().values()].filter(item => !assignedIds.has(item.id));
  });

  private readonly selectedAssignedItems = computed(() => {
    const assignedIds = this.assignedIdSet();
    return [...this.selection().values()].filter(item => assignedIds.has(item.id));
  });

  readonly selectionSummary = computed(() => {
    const selected = [...this.selection().values()];
    if (!selected.length) {
      return undefined;
    }

    const shownIds = new Set(
      [...this.filteredUnassignedItems(), ...(this.filteredAssignedItems() ?? [])].map(item => item.id),
    );
    const notVisible = selected.filter(item => !shownIds.has(item.id)).length;

    return {
      notVisible,
      label: notVisible
        ? `${selected.length} ausgewählt, davon ${notVisible} nicht sichtbar`
        : `${selected.length} ausgewählt`,
    };
  });

  private scroll$ = new Subject<void>();

  private search$ = new Subject<string>();

  constructor() {
    super();

    watch(this.searchText, () => {
      if (this.behaviour().isPaginated) {
        this.page.set(0);
        this.items.set([]);
      }
    });

    this.scroll$
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.isScrollLoadQueued = false;

        // Erneut pruefen statt nur beim Enqueue: waehrend der 500 ms kann eine
        // Suche `page` auf 0 zurueckgesetzt und Seite 0 angefordert haben. Ein
        // Hochzaehlen wuerde die noch laufende Seite 0 ueberholen und ihre
        // Antwort damit verwerfen lassen.
        if (this.isLoadingItems) {
          return;
        }

        this.page.update(p => p + 1);
      });

    this.search$
      .pipe(debounceTime(500))
      .subscribe((value) => {
        this.searchText.set(value);
      });

    effect(async () => {
      const behaviour = this.behaviour();
      const requestId = ++this.loadRequestId;
      this.isLoadingItems = true;

      try {
        if (behaviour.isPaginated) {
          const paginated = behaviour as unknown as PaginatedPickListBehaviour<Item>;
          // page() festhalten: nach dem await kann das Signal schon eine neuere Seite
          // tragen, der set-vs-update-Zweig unten braucht aber die angefragte.
          // (Getrackt wird das Signal ohnehin, der Lesezugriff steht vor dem await.)
          const requestedPage = this.page();
          const { items } = await paginated.getAllItemsWithPagination({
            page: requestedPage,
            size: this.pageSize,
            search: this.searchText(),
          });

          if (requestId !== this.loadRequestId) {
            return;
          }

          if (requestedPage === 0) {
            this.items.set(items);
          } else {
            this.items.update(old => [...old, ...items]);
          }
        } else {
          const unpaginated = behaviour as unknown as UnpaginatedPickListBehaviour<Item>;
          const items = await unpaginated.getAllItems();

          if (requestId !== this.loadRequestId) {
            return;
          }

          this.items.set(items);
          this.deselectUnavailable(items);
        }
      } finally {
        if (requestId === this.loadRequestId) {
          this.isLoadingItems = false;
        }
      }
    });
  }

  ngOnInit(): void {
    const initialSearch = this.initialSearch().trim();
    if (initialSearch) {
      this.searchText.set(initialSearch);
    }
  }

  isAssignedElsewhere(item: Item): boolean {
    return this.behaviour().getAssignmentType(item) === AssignmentType.ASSIGNED_ELSEWHERE && !this.assignedItemIds().includes(item.id);
  }

  /**
   * Blendet anderweitig zugewiesene Elemente aus. Wird nichts ausgeblendet, kommt
   * das Original-Array zurueck, damit die Referenz stabil bleibt.
   */
  private withoutAssignedElsewhere(items: Item[]): Item[] {
    const b = this.behaviour();
    const visible = items.filter(item => b.getAssignmentType(item) !== AssignmentType.ASSIGNED_ELSEWHERE);
    return visible.length === items.length ? items : visible;
  }

  /**
   * Sortiert alphabetisch. Paginierte Listen kommen bereits sortiert vom Server
   * und wuerden durch lokales Sortieren nur seitenweise umgeordnet.
   */
  private sortItems(items: Item[]): Item[] {
    if (this.behaviour().isPaginated) {
      return items;
    }

    return [...items].sort((a, b) =>
      this.displayName(a).localeCompare(this.displayName(b), 'de', { sensitivity: 'base' }),
    );
  }

  /**
   * Filtert nach dem Suchtext. Bei paginierten Listen uebernimmt das der Server.
   * Ohne Suchtext kommt das Eingabe-Array unveraendert zurueck.
   */
  private applySearch(items: Item[]): Item[] {
    const b = this.behaviour();

    if (b.isPaginated) {
      return items;
    }

    const tokens = tokenizeSearch(this.searchText());
    if (tokens.length === 0) {
      return items;
    }

    return items.filter(item => matchesTokens(tokens, ...(b.getSearchableFields?.(item) ?? [this.displayName(item)])));
  }

  displayName(item: Item): string {
    return this.behaviour().getDisplayName(item);
  }

  firstAdditionalInfo(item: Item): string {
    return this.behaviour().getFirstAdditionalInfo?.(item) ?? '';
  }

  secondAdditionalInfo(item: Item): string {
    return this.behaviour().getSecondAdditionalInfo?.(item) ?? '';
  }

  thirdAdditionalInfo(item: Item): string {
    return this.behaviour().getThirdAdditionalInfo?.(item) ?? '';
  }

  searchTextChange(value: string) {
    this.search$.next(value);
  }

  /**
   * Beim Deaktivieren den Suchtext leeren. Die Suchleiste wird im Read-only-Zweig
   * nicht gerendert, das Signal behielte sonst seinen Wert — und
   * `filteredAssignedItems` wuerde die Read-only-Liste nach einem Text filtern,
   * den niemand mehr sehen oder loeschen kann.
   */
  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);

    if (isDisabled) {
      this.searchText.set('');
      this.clearSelection();
    }
  }

  override writeValue(value: Item[] | undefined): void {
    super.writeValue(value);
    this.clearSelection();
  }

  moveSelectedToAssigned(): void {
    const toAssign = this.selectedUnassignedItems();
    if (!toAssign.length) {
      return;
    }

    this.value = [...(this.value ?? []), ...toAssign];
    this.deselect(toAssign);
  }

  moveSelectedToUnassigned(): void {
    const toUnassign = this.selectedAssignedItems();
    if (!toUnassign.length) {
      return;
    }

    const unassignIds = new Set(toUnassign.map(item => item.id));
    this.value = (this.value ?? []).filter(item => !unassignIds.has(item.id));
    this.deselect(toUnassign);
  }

  isSelected(item: Item): boolean {
    return this.selection().has(item.id);
  }

  onSelectionChange(checked: boolean, item: Item): void {
    this.selection.update(selection => {
      const next = new Map(selection);
      if (checked) {
        next.set(item.id, item);
      } else {
        next.delete(item.id);
      }
      return next;
    });
  }

  clearSelection(): void {
    this.selection.set(new Map());
  }

  private deselect(items: Item[]): void {
    this.selection.update(selection => {
      const next = new Map(selection);
      items.forEach(item => next.delete(item.id));
      return next;
    });
  }

  private deselectUnavailable(items: Item[]): void {
    const availableIds = new Set([...items.map(item => item.id), ...this.assignedItemIds()]);
    const unavailable = [...this.selection().values()].filter(item => !availableIds.has(item.id));
    if (unavailable.length) {
      this.deselect(unavailable);
    }
  }

  toggleAssignedItems() {
    this.showFromOtherScopes.update(show => !show);
  }

  onUnassignedScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 150;

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {

      // Unpaginierte Listen rendern immer komplett, nachladen ist nur hier noetig.
      if (!this.behaviour().isPaginated) {
        return;
      }

      if (this.isLoadingItems || this.isScrollLoadQueued) return;

      this.isScrollLoadQueued = true;

      this.scroll$.next();
    }
  }

  isChecked(item: Item): boolean {
    return this.value?.some(i => i.id === item.id) ?? false;
  }

  async moveAlreadyAssignedToThis(event: Event, item: Item): Promise<void> {
    event.stopPropagation();
    event.preventDefault();

    const dialogRef = await this.confirmDialog();
    if (!dialogRef) {
      return;
    }

    const current = this.valueSignal();
    this.value = [...(current ?? []), item];
    this.deselect([item]);
  }

  async confirmDialog(): Promise<boolean> {
    const dialogRef = this.nbDialogService.open(GenericDialogComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        header: 'Element verschieben',
        message: 'Möchten Sie dieses Element hier zuweisen?',
        status: 'warning',
      },
    });

    return firstValueFrom(dialogRef.onClose);
  }

}
